<app-button2
  cdkOverlayOrigin
  #origin="cdkOverlayOrigin"
  appearance="secondary"
  size="m"
  [disabled]="disabled()"
  [ariaExpanded]="opened()"
  [ariaControls]="panelId"
  ariaHaspopup="dialog"
  (click)="toggleOpen()"
>
  @if (triggerTemplate(); as customTrigger) {
    <ng-container [ngTemplateOutlet]="customTrigger" [ngTemplateOutletContext]="triggerContext()" />
  } @else {
    {{ label() }}
    <app-icon name="sapphire-chevron-down" size="s" />
  }
</app-button2>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="origin"
  [cdkConnectedOverlayOpen]="opened()"
  [cdkConnectedOverlayPositions]="positions"
  [cdkConnectedOverlayHasBackdrop]="true"
  cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
  [cdkConnectedOverlayViewportMargin]="8"
  [cdkConnectedOverlayPush]="true"
  (attach)="focusFirstControl()"
  (backdropClick)="close()"
  (overlayKeydown)="onOverlayKeydown($event)"
>
  <div #panel [id]="panelId" class="multi-select-panel" role="dialog" tabindex="-1" [attr.aria-label]="label()">
    @if (topText()) {
      <p class="multi-select-help">{{ topText() }}</p>
    }

    @if (visiblePinnedItems().length) {
      <div class="multi-select-pinned">
        @for (item of visiblePinnedItems(); track item.id) {
          <ng-container [ngTemplateOutlet]="row" [ngTemplateOutletContext]="{ $implicit: item }" />
        }
      </div>
    }

    @if (searchable() || visibleItems().length || loading() || errorText() || !visiblePinnedItems().length) {
      <div class="multi-select-main" [class.multi-select-separated]="visiblePinnedItems().length > 0">
        @if (searchable()) {
          <label class="multi-select-search">
            <span class="sr-only">{{ searchPlaceholder() }}</span>
            <app-icon name="sapphire-search" size="m" />
            <input
              #searchField
              type="search"
              [value]="searchQuery()"
              [placeholder]="searchPlaceholder()"
              (input)="onSearchInput($event)"
            />
          </label>
        }
        @if (errorText()) {
          <div class="multi-select-message" role="alert">
            <span>{{ errorText() }}</span>
            <app-button2 appearance="secondary-text" size="s" (click)="retry.emit()">Retry</app-button2>
          </div>
        } @else if (loading()) {
          <p class="multi-select-message" role="status">Loading…</p>
        } @else {
          <div class="multi-select-scroll">
            @for (item of visibleItems(); track item.id) {
              <ng-container [ngTemplateOutlet]="row" [ngTemplateOutletContext]="{ $implicit: item }" />
            } @empty {
              @if (!visiblePinnedItems().length || searchQuery()) {
                <p class="multi-select-message">{{ searchQuery() ? noResultsText() : emptyText() }}</p>
              }
            }
            @if (hasMore() || loadingMore()) {
              <button type="button" class="multi-select-load-more" [disabled]="loadingMore()" (click)="loadMore.emit()">
                {{ loadingMore() ? 'Loading…' : 'More members' }}
              </button>
            }
          </div>
        }
      </div>
    }

    @if (bottomText()) {
      <p class="multi-select-help multi-select-bottom">{{ bottomText() }}</p>
    }

    @if (action(); as footerAction) {
      <div class="multi-select-footer">
        <app-button2
          size="m"
          appearance="secondary"
          [disabled]="footerAction.disabled || footerAction.pending"
          (click)="onAction()"
        >
          {{ footerAction.pending ? 'Working…' : footerAction.label }}
        </app-button2>
      </div>
    }
  </div>
</ng-template>

<ng-template #row let-item>
  <label
    class="multi-select-row"
    [class.multi-select-row-disabled]="selectionReadonly() || item.disabled"
    [class.multi-select-row-selected]="selectedIdSet().has(item.id)"
  >
    @if (rowTemplate(); as customRow) {
      <ng-container
        [ngTemplateOutlet]="customRow"
        [ngTemplateOutletContext]="{ $implicit: item, selected: selectedIdSet().has(item.id) }"
      />
    } @else {
      @if (item.avatar; as avatar) {
        <span
          class="multi-select-avatar"
          [class.multi-select-avatar-blue]="avatar.tone === 'blue'"
          [class.multi-select-avatar-orange]="avatar.tone === 'orange'"
          [class.multi-select-avatar-green]="avatar.tone === 'green'"
          [class.multi-select-avatar-violet]="avatar.tone === 'violet'"
          [class.multi-select-avatar-lime]="avatar.tone === 'lime'"
          [style.background-color]="avatar.backgroundColor"
          [style.color]="avatar.textColor"
        >
          @if (avatar.imageUrl) {
            <img [src]="avatar.imageUrl" alt="" />
          } @else {
            {{ avatar.initials }}
          }
        </span>
      }
      <span class="multi-select-row-text">
        <span class="multi-select-row-label">{{ item.label }}</span>
        @if (item.description) {
          <span class="multi-select-row-description">{{ item.description }}</span>
        }
      </span>
    }
    <input
      type="checkbox"
      class="sr-only multi-select-checkbox-input"
      [checked]="selectedIdSet().has(item.id)"
      [disabled]="selectionReadonly() || selectionPending() || item.disabled"
      [attr.aria-label]="item.label"
      (change)="onItemChange(item, $event)"
    />
    <span
      class="multi-select-checkbox-icon"
      [class.multi-select-checkbox-icon-checked]="selectedIdSet().has(item.id)"
      [class.multi-select-checkbox-icon-readonly]="selectionReadonly() || item.disabled"
    >
      <app-icon [name]="selectedIdSet().has(item.id) ? 'sapphire-checkbox-on' : 'sapphire-checkbox-off'" size="m" />
    </span>
  </label>
</ng-template>
